import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import Chart from 'chart.js/auto';

// It's recommended to move this to an environment variable
const PASSWORD = process.env.REACT_APP_DASHBOARD_PASSWORD || 'aiatuserstat';

export default function UserMetricsDashboard() {
  const [authorized, setAuthorized] = useState(false);
  const [inputPass, setInputPass] = useState('');
  const [passError, setPassError] = useState('');

  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const aggOptions = ['hour', 'day', 'week', 'month'];
  const windowMap = {
    hour: [24, 48, 72],
    day: [7, 14, 30],
    week: [8, 12, 26],
    month: [12, 24, 36],
  };

  const [agg, setAgg] = useState('hour');
  const [range, setRange] = useState(windowMap.hour[0]);
  const chartRef = useRef();

  // Fetch metrics only after authorization
  useEffect(() => {
    if (!authorized) return;

    setLoading(true);
    axios
      .get('/api/users/metrics', {
        headers: { 'x-dashboard-password': PASSWORD },
      })
      .then(res => {
        setRaw(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err);
        setLoading(false);
      });
  }, [authorized]);

  const handleSubmit = e => {
    e.preventDefault();
    if (inputPass === PASSWORD) {
      setAuthorized(true);
      setPassError('');
    } else {
      setPassError('Incorrect password');
    }
  };

  // Show password prompt if not authorized
  if (!authorized) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <form onSubmit={handleSubmit} className="bg-gray-800 p-8 rounded-xl shadow-lg">
          <h2 className="mb-4 text-xl font-medium text-white">Enter Dashboard Password</h2>
          <input
            type="password"
            value={inputPass}
            onChange={e => setInputPass(e.target.value)}
            placeholder="Password"
            className="w-full mb-4 px-4 py-2 rounded-md bg-gray-700 text-white focus:outline-none"
          />
          {passError && <p className="text-red-500 mb-2">{passError}</p>}
          <button
            type="submit"
            className="w-full px-4 py-2 bg-indigo-600 rounded-md text-white hover:bg-indigo-700 transition"
          >
            Submit
          </button>
        </form>
      </div>
    );
  }

  // Render dashboard after successful authorization
  if (loading) return <p className="py-10 text-center text-gray-400">Loading metrics…</p>;
  if (error) return <p className="py-10 text-center text-red-500">Error loading data</p>;
  if (!raw) return null;

  const seriesMap = {
    hour: raw.perHour,
    day: raw.perDay,
    week: raw.perWeek,
    month: raw.perMonth,
  };
  const allPoints = seriesMap[agg] || [];
  const points = allPoints.slice(-range);

  const data = {
    labels: points.map(p => p.bucket),
    datasets: [
      {
        label: 'New users',
        data: points.map(p => p.count),
        backgroundColor: context => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, 'rgba(79,70,229,0.8)');
          gradient.addColorStop(1, 'rgba(79,70,229,0.3)');
          return gradient;
        },
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        padding: 8,
        cornerRadius: 4,
        titleFont: { family: 'Inter', weight: '600' },
        bodyFont: { family: 'Inter' },
      },
    },
    scales: {
      x: {
        ticks: { color: '#E5E7EB', maxRotation: 0, autoSkip: true },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: '#E5E7EB' },
        grid: { color: 'rgba(255,255,255,0.1)' },
      },
    },
    layout: {
      padding: { top: 10, right: 10, bottom: 10, left: 10 },
    },
  };

  const titles = {
    hour: `New users per hour (last ${range} h)`,
    day: `New users per day (last ${range} d)`,
    week: `New users per week (last ${range} w)`,
    month: `New users per month (last ${range} mo)`,
  };

  return (
    <div className="mx-auto max-w-screen-xl space-y-10 p-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total users" value={raw.totalUsers} />
        <StatCard label="Last 24 h" value={raw.totals.last24h} />
        <StatCard label="Last 7 d" value={raw.totals.last7d} />
        <StatCard label="Last 30 d" value={raw.totals.last30d} />
      </div>

      <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
        <div className="flex gap-1 rounded-xl bg-white/10 p-1 backdrop-blur">
          {aggOptions.map(opt => (
            <button
              key={opt}
              onClick={() => {
                setAgg(opt);
                setRange(windowMap[opt][0]);
              }}
              className={`px-4 py-1.5 text-sm rounded-lg transition \
                ${agg === opt                  ? 'bg-white/90 text-gray-800 font-semibold shadow-lg'                  : 'text-black/80 hover:bg-white/20'}`}
            >
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </button>
          ))}
        </div>

        <label className="text-sm font-medium text-white/70">
          Look-back{' '}
          <select
            value={range}
            onChange={e => setRange(+e.target.value)}
            className="ml-1 rounded-md bg-gray-900 px-12 py-1 text-white shadow-inner backdrop-blur"
          >
            {windowMap[agg].map(v => (
              <option key={v} value={v}>
                {agg === 'hour'
                  ? `${v} h`
                  : agg === 'day'
                  ? `${v} d`
                  : agg === 'week'
                  ? `${v} w`
                  : `${v} mo`}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="rounded-2xl bg-gray-800 p-6 shadow-2xl">
        <h2 className="mb-4 text-lg font-medium text-white">{titles[agg]}</h2>
        <div className="relative h-80">
          <Bar ref={chartRef} data={data} options={options} />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl border border-white/20 bg-gray-900/50 p-6 text-center backdrop-blur shadow-xl">
      <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-2 text-3xl font-semibold tabular-nums text-white">{value}</p>
    </div>
  );
}
